import { FormProvider, useController, useForm } from 'react-hook-form';
import { useRescheduleLogic } from './hooks/useRescheduleLogic';
import RescheduleDirectFormUI from './formUi';
import { useTherapistSupportNumber } from 'domains/support/therapistsNumber';
import { useNavigate } from 'react-router-dom';
import { useDisplayTimezone } from 'domains/timezone/useDisplayTimezone';
import { yupResolver } from '@hookform/resolvers/yup';
import { getLocalizedSchema } from './schema';
import { useEffect } from 'react';
import { FormLayout } from '@expressable/syntax-ds';
import { useTranslation } from 'react-i18next';

type RescheduleProps = {
  loading?: boolean;
};

export const RescheduleDirectForm = ({ loading = false }: RescheduleProps) => {
  const { data: therapistPhoneNumberData } = useTherapistSupportNumber();
  const { t } = useTranslation();
  const navigator = useNavigate();
  const { displayTimezone, format: formatInTimezone } = useDisplayTimezone();

  const {
    rescheduleInformation,
    selectedDayIndex,
    currentSlots,
    onChangeDay,
    dayOptions,
    onLoadMoreChipsCallback,
    timeSlot,
    setTimeSlot,
    availableTimesQuery,
    clientId,
    rescheduleId,
  } = useRescheduleLogic();

  const methods = useForm<{
    dayIndex?: number;
    timeIndex?: number;
  }>({
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    resolver: yupResolver(getLocalizedSchema(t)),
    disabled: loading,
    defaultValues: {
      dayIndex: selectedDayIndex,
      timeIndex: timeSlot ? currentSlots?.indexOf(timeSlot) : undefined,
    },
  });
  const { handleSubmit, control, setValue } = methods;

  useEffect(() => {
    if (!loading) {
      setValue('dayIndex', selectedDayIndex ?? undefined);
      setValue('timeIndex', timeSlot ? currentSlots?.indexOf(timeSlot) : undefined);
    }
  }, [selectedDayIndex, timeSlot, currentSlots, loading, setValue]);

  const dayController = useController({
    name: 'dayIndex',
    control,
  });

  const timeController = useController({
    name: 'timeIndex',
    control,
  });

  const onSubmit = async () => {
    navigator(`/reschedule/${clientId}/${rescheduleId}/confirm`);
  };

  return (
    <>
      <FormProvider {...methods}>
        <FormLayout onSubmit={handleSubmit(onSubmit)}>
          <RescheduleDirectFormUI
            loading={loading}
            onChangeDay={onChangeDay}
            onLoadMoreChipsCallback={onLoadMoreChipsCallback}
            rescheduleInformation={rescheduleInformation}
            selectedDayIndex={selectedDayIndex}
            currentSlots={currentSlots}
            dayOptions={dayOptions}
            timeSlot={timeSlot}
            setTimeSlot={setTimeSlot}
            therapistPhoneNumberData={therapistPhoneNumberData}
            displayTimezone={displayTimezone}
            dayController={dayController}
            timeController={timeController}
            formatInTimezone={formatInTimezone}
            availableTimesQuery={availableTimesQuery}
          />
        </FormLayout>
      </FormProvider>
    </>
  );
};
